import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';

// Hero Section with 2x2 image grid
const HeroSection = () => {
  return (
    <section className="relative bg-black-deep" style={{ height: '100vh', maxHeight: '900px' }}>
      {/* 2x2 Image Grid - True grid with gaps */}
      <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-[2px] bg-black">
        {/* home-hero-1: Top Left */}
        <div 
          className="relative bg-cover"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_luxury-pairing/artifacts/wmyt86jk_Home%201.jpg')`,
            backgroundPosition: 'center 20%',
          }}
          data-placeholder="home-hero-1"
        >
          <div className="absolute inset-0 bg-black/15"></div>
        </div>
        
        {/* home-hero-2: Top Right */}
        <div 
          className="relative bg-cover"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_luxury-pairing/artifacts/2frtnx0o_55e2e2a7-6d1d-444a-9263-bcc500ea1963.png')`,
            backgroundPosition: 'center 30%',
          }}
          data-placeholder="home-hero-2"
        >
          <div className="absolute inset-0 bg-black/15"></div>
        </div>
        
        {/* home-hero-3: Bottom Left */}
        <div 
          className="relative bg-cover"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_luxury-pairing/artifacts/kbczr8a3_Home%203.png')`,
            backgroundPosition: 'center 25%',
          }}
          data-placeholder="home-hero-3"
        >
          <div className="absolute inset-0 bg-black/15"></div>
        </div>
        
        {/* home-hero-4: Bottom Right */}
        <div 
          className="relative bg-cover"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_luxury-pairing/artifacts/ktunrb41_a7893302-9acf-4bda-b79b-b901ff28e943.png')`,
            backgroundPosition: 'center 25%',
          }}
          data-placeholder="home-hero-4"
        >
          <div className="absolute inset-0 bg-black/15"></div>
        </div>
      </div>
      
      {/* Uniform dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/25"></div>
      
      {/* Content - centered */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative z-10 text-center px-6">
          <h1 className="font-serif text-[2.75rem] md:text-[3.5rem] lg:text-[4rem] text-cream font-light leading-[1.1] tracking-normal mb-3">
            Private Matchmaking
            <br />
            <span className="italic">for Extraordinary Lives</span>
          </h1>
          
          <p className="font-sans text-cream/85 text-sm md:text-base max-w-xl mx-auto mb-6 leading-relaxed">
            <span className="hidden md:inline">A discreet, invitation-only matchmaking experience for successful men<br />and magnetic women who value chemistry, intention, and lasting love.</span>
            <span className="md:hidden">A discreet, invitation-only matchmaking experience for successful men and magnetic women who value chemistry, intention, and lasting love.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/apply" className="btn-gold-pill-sm">
              Apply Privately
            </Link>
            <Link to="/lounge" className="btn-dark-pill-sm">
              Explore the Diamond Club Lounge
            </Link>
            <Link to="/testimonials" className="btn-gold-pill-sm" data-testid="hero-testimonials-btn">
              Testimonials
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: 'APPLY PRIVATELY',
      description: 'Share your details and preferences, then apply through a simple, confidential application designed with care and discretion.',
    },
    {
      number: '02',
      title: 'WE CURATE',
      description: 'Our matchmakers meticulously review your application, focusing on your values, lifestyle and relationship goals to identify aligned introductions.',
    },
    {
      number: '03',
      title: 'CONNECT MEANINGFULLY',
      description: 'We make tailored introductions to those who share your vision for a fulfilled, lasting relationship.',
    },
  ];

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden">
      {/* Background - subtle images with strong dark overlay */}
      <div className="absolute inset-0 bg-black-deep">
        {/* Left side decorative image */}
        <div 
          className="absolute left-0 top-0 h-full w-1/5 bg-cover bg-center opacity-25"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=800')`,
          }}
          data-placeholder="process-bg-left"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black-deep/50 to-black-deep"></div>
        </div>
        
        {/* Right side decorative image */}
        <div 
          className="absolute right-0 bottom-0 h-3/5 w-1/4 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/4611741/pexels-photo-4611741.jpeg?auto=compress&cs=tinysrgb&w=800')`,
          }}
          data-placeholder="process-bg-right"
        >
          <div className="absolute inset-0 bg-gradient-to-l from-black-deep/50 to-black-deep"></div>
        </div>
        
        {/* Grain overlay */}
        <div className="absolute inset-0 grain-overlay"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-serif text-2xl md:text-3xl text-cream tracking-widest uppercase mb-3 font-normal">
            Our Simple Process
          </h2>
          <p className="font-sans text-cream-muted text-sm max-w-lg mx-auto leading-relaxed">
            An exclusive, members-only experience designed for those who
            value luxury, connection, and curated chemistry.
          </p>
        </div>
        
        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-10 mb-12">
          {steps.map((step, index) => (
            <div key={step.number} className="text-center relative">
              {/* Connecting line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-[60%] w-full h-[1px] bg-gold/40"></div>
              )}
              
              {/* Gold circle with number */}
              <div className="gold-circle-sm mx-auto mb-4">
                {step.number}
              </div>
              
              {/* Title */}
              <h3 className="font-serif text-base md:text-lg text-cream tracking-[0.2em] uppercase mb-3 font-normal">
                {step.title}
              </h3>
              
              {/* Description */}
              <p className="font-sans text-cream-muted text-xs leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
        
        {/* Bottom bullet points */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-12 mt-12 pt-10 border-t border-gold/25">
          {/* Left column */}
          <div className="space-y-3">
            <p className="font-serif text-cream italic text-xl leading-relaxed gold-bullet">
              We meet every member face-to-face or via video call to verify their identity and ensure they look like their photos.
            </p>
            <p className="font-serif text-cream italic text-xl leading-relaxed gold-bullet">
              We conduct extensive background checks on all applicants.
            </p>
          </div>
          
          {/* Right column */}
          <div className="space-y-3">
            <p className="font-serif text-cream italic text-xl leading-relaxed gold-bullet">
              We curate matches based on compatibility in values, lifestyle, and endgame.
            </p>
            <p className="font-serif text-cream italic text-xl leading-relaxed gold-bullet">
              This is not a numbers game: every introduction is thoughtfully&mdash;and purposefully&mdash;arranged.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Section - Between Our Simple Process and Apply Privately
const VideoSection = () => {
  return (
    <section className="relative py-16 lg:py-24 bg-black-deep grain-overlay">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="relative w-full aspect-video rounded-sm overflow-hidden border border-gold/20">
          <iframe
            src="https://www.youtube-nocookie.com/embed/XJbMivPSYUg?rel=0&modestbranding=1&showinfo=0"
            title="Diamond Club Matchmaking"
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
};

// Apply Section - Header with CTA button (form moved to /apply page)
const ApplySection = () => {
  return (
    <section id="apply" className="relative py-24 lg:py-32 bg-black-soft grain-overlay">
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="font-serif text-display-sm md:text-display text-cream tracking-wide mb-4">
            Apply Privately
          </h2>
          <p className="font-sans text-cream-muted text-base mb-8">
            Share your details. We&apos;ll review and follow up privately.
          </p>
          
          {/* CTA Button */}
          <Link to="/apply" className="btn-gold-pill inline-block px-10 py-4">
            Apply Privately
          </Link>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  useEffect(() => {
    document.title = 'Diamond Club Matchmaking';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'A discreet, invitation-only matchmaking experience for successful men and magnetic women seeking meaningful, lasting relationships.');
    }
  }, []);

  return (
    <Layout>
      <HeroSection />
      <ProcessSection />
      <VideoSection />
      <ApplySection />
    </Layout>
  );
};

export default Home;
